import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Fade,
  Slide,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn, getCurrentUser } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  // Check for success message from password reset
  useEffect(() => {
    if (location.state?.message) {
      setError('');
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await signIn(formData.email, formData.password);
      
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        // Redirect to change password page with session
        navigate('/change-password', { 
          state: { 
            email: formData.email,
            session: response.Session 
          }
        });
        return;
      }

      if (response.AuthenticationResult) {
        // Get user info after successful login
        try {
          const userResponse = await getCurrentUser();
          if (userResponse) {
            const userAttributes = {};
            userResponse.UserAttributes.forEach(attr => {
              userAttributes[attr.Name] = attr.Value;
            });
            setUser(userAttributes);
            
            // Redirect to the page they tried to visit or home
            const redirectTo = location.state?.from?.pathname || '/home';
            navigate(redirectTo, { replace: true });
          }
        } catch (userError) {
          console.error('Error getting user info:', userError);
          setError('Failed to get user information. Please try again.');
        }
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      {/* Ocean Video Background */}
      <Box
        component="video"
        autoPlay
        muted
        loop
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: -2
        }}
      >
        <source src="/loginwaves.mp4" type="video/mp4" />
      </Box>

      {/* Semi-transparent overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(144,224,239,0.2) 50%, rgba(0,0,0,0.4) 100%)',
          zIndex: -1
        }}
      />

      {/* Glassmorphism Login Card */}
      <Card
        sx={{
          minWidth: { xs: '90%', sm: 400 },
          maxWidth: 450,
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          p: 2
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* App Branding */}
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 600,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                mb: 1
              }}
            >
              Waves Within
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 300,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              Mantras • Meditation • Sound
            </Typography>
          </Box>

          {/* Form */}
          <Fade in={true} timeout={500}>
            <Box component="form" onSubmit={handleSubmit}>
              <Typography
                variant="h5"
                textAlign="center"
                sx={{ color: 'white', mb: 3, fontWeight: 500 }}
              >
                Welcome Back
              </Typography>

              {error && (
                <Typography
                  color="error"
                  sx={{
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(211, 47, 47, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  {error}
                </Typography>
              )}

              {location.state?.message && (
                <Typography
                  color="success"
                  sx={{
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  {location.state.message}
                </Typography>
              )}

              <Slide direction="right" in={true} timeout={600}>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#90E0EF'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#90E0EF'
                      }
                    }
                  }}
                />
              </Slide>

              <Slide direction="left" in={true} timeout={700}>
                <TextField
                  fullWidth
                  name="password"
                  type="password"
                  label="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#90E0EF'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#90E0EF'
                      }
                    }
                  }}
                />
              </Slide>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': {
                          color: '#90E0EF'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    color: '#90E0EF',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.5,
                  background: 'linear-gradient(45deg, #90E0EF 30%, #F4A261 90%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 20px rgba(144, 224, 239, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #7DD3FC 30%, #FB923C 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 25px rgba(144, 224, 239, 0.6)'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>

              <Box textAlign="center">
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Don't have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/signup')}
                    sx={{
                      color: '#90E0EF',
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Fade>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage; 